import {Pipe, PipeTransform} from '@angular/core';
import {DomSanitizer, SafeHtml} from '@angular/platform-browser';
import DOMPurify from 'dompurify';

/**
 * SafeHtmlPipe - Sanitizes HTML before bypassing Angular security
 *
 * SECURITY: This pipe uses DOMPurify to whitelist safe HTML tags and attributes
 * before bypassing Angular's XSS protection. This prevents XSS attacks while
 * allowing safe formatting tags like <p>, <b>, <i>, etc.
 *
 * Allowed tags: p, b, i, strong, em, u, br, ul, ol, li, span, div
 * Allowed attributes: class (limited to safe classes only)
 * Blocked: script, iframe, object, embed, style tags, javascript: URLs, event handlers
 */
@Pipe({
    name: 'safeHtml',
    standalone: false
})
export class SafeHtmlPipe implements PipeTransform {
  constructor(private sanitizer: DomSanitizer) {}

  transform(value: string): SafeHtml {
    if (!value) {
      return '';
    }

    // Configure DOMPurify with strict whitelist
    const sanitized = DOMPurify.sanitize(value, {
      ALLOWED_TAGS: [
        'p', 'br', 'b', 'i', 'strong', 'em', 'u',
        'ul', 'ol', 'li',
        'span', 'div',
        'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
        'a' // For links (href will be validated)
      ],
      ALLOWED_ATTR: [
        'class', // For styling
        'href',  // For links (will be validated)
        'target' // For links (limited to _blank)
      ],
      ALLOWED_URI_REGEXP: /^(?:(?:https?|mailto):)/i, // Only allow https, http, mailto
      ALLOW_DATA_ATTR: false,
      KEEP_CONTENT: true, // Keep text content even if tags are stripped
      RETURN_TRUSTED_TYPE: false
    });

    // Additional security: Ensure no javascript: or data: URLs slipped through
    const safeHtml = sanitized.replace(/javascript:/gi, '')
                              .replace(/data:text\/html/gi, '');

    // Now it's safe to bypass Angular security
    return this.sanitizer.bypassSecurityTrustHtml(safeHtml);
  }
}

